import { OpenCageProvider } from "leaflet-geosearch";
import { LOCATION_BASE_URL } from "../constants";
import type { ApiCoordinates, ApiLocation, LocationResult, OpenCageResult } from "../types/Location";
import type { SearchResult } from "leaflet-geosearch/dist/providers/provider.js";
import { isEmpty, isNil } from "lodash-es";
import { isNotEmpty } from "./stringUtil";

type OpenCageOptions = NonNullable<ConstructorParameters<typeof OpenCageProvider>[0]>;

export class LocationProvider extends OpenCageProvider {
  constructor(options: OpenCageOptions = {}) {
    super({ ...options });
    this.searchUrl = `${LOCATION_BASE_URL}/opencage/forward`;
  }
}

export const openCageToLocationResult = (result: SearchResult<OpenCageResult>): LocationResult => {
  const addressComponents = result.raw.components;

  const type = addressComponents._type;
  return {
    coordinates: {
      latitude: result.y,
      longitude: result.x,
    },
    location: {
      descriptiveName: type ? addressComponents.unmappedFields[type] : undefined,
      road: addressComponents.road,
      city: addressComponents.town ?? addressComponents.village ?? addressComponents.city,
      postcode: addressComponents.postcode,
      houseNumber: addressComponents.house_number,
      country: addressComponents.country,
    },
    formattedValue: result.label,
  };
};

export const locationToHumanReadableString = (locationResult?: LocationResult): string => {
  if (!isNil(locationResult?.location)) {
    const formatted = formatLocation(locationResult.location);
    if (!isEmpty(formatted.trim())) {
      return formatted;
    }
  }
  if (!isNil(locationResult?.coordinates)) {
    return `${locationResult.coordinates?.latitude}, ${locationResult.coordinates?.longitude}`;
  }
  return locationResult?.formattedValue ?? "";
};

const formatLocation = (location: ApiLocation): string =>
  [
    location.descriptiveName ?? "",
    `${location.road ?? ""} ${location.houseNumber ?? ""}`.trim(),
    `${location.postcode ?? ""} ${location.city ?? ""}`.trim(),
    location.country ?? "",
  ]
    .filter((i) => isNotEmpty(i))
    .join(", ");

export const normalizeLongitude = (longitude: number): number => {
  if (longitude >= -180 && longitude <= 180) {
    return longitude;
  }

  const normalized = ((((longitude + 180) % 360) + 360) % 360) - 180;
  return normalized === -180 && longitude > 0 ? 180 : normalized;
};

export const coordinatesToHumanReadableString = (coordinates: ApiCoordinates, locale: string): string => {
  const formatCoordinate = (value: number): string =>
    Math.abs(value).toLocaleString(locale, {
      useGrouping: false,
      maximumFractionDigits: 4,
    });

  const latitude = `${formatCoordinate(coordinates.latitude)}° ${coordinates.latitude >= 0 ? "N" : "S"}`;
  const longitude = `${formatCoordinate(coordinates.longitude)}° ${coordinates.longitude >= 0 ? "E" : "W"}`;
  return `${latitude}, ${longitude}`;
};
